import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../env';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  AddItemRequest,
  CreateShoppingListRequest,
  FrequentlyBoughtItem,
  ProductListPresence,
  RecentTripItem,
  ShoppingList,
  ShoppingListItem,
  ShoppingListWithItems,
  ShoppingTrip,
  UpdateShoppingListItemRequest,
} from '../../models/shopping-list';

@Injectable({
  providedIn: 'root'
})
export class ShoppingService {

  private readonly http = inject(HttpClient);

  // ── Legacy restock links ────────────────────────────────────────────────────
  restockLinks(householdId: number): Observable<Map<string, string>> {
    return this.http.get<Map<string, string>>(`${environment.apiUrl}/households/${householdId}/restock-links`);
  }

  // ── Lists ───────────────────────────────────────────────────────────────────
  getLists(householdId: number): Observable<ShoppingList[]> {
    return this.http.get<ShoppingList[]>(`${environment.apiUrl}/households/${householdId}/shopping-lists`);
  }

  createList(householdId: number, body: CreateShoppingListRequest): Observable<ShoppingList> {
    return this.http.post<ShoppingList>(`${environment.apiUrl}/households/${householdId}/shopping-lists`, body);
  }

  getList(listId: number): Observable<ShoppingListWithItems> {
    return this.http.get<ShoppingListWithItems>(`${environment.apiUrl}/shopping-lists/${listId}`);
  }

  updateList(listId: number, body: Partial<CreateShoppingListRequest>): Observable<ShoppingList> {
    return this.http.patch<ShoppingList>(`${environment.apiUrl}/shopping-lists/${listId}`, body);
  }

  activateList(householdId: number, listId: number): Observable<ShoppingList> {
    return this.http.put<ShoppingList>(`${environment.apiUrl}/households/${householdId}/shopping-lists/${listId}/activate`, {});
  }

  deleteList(listId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/shopping-lists/${listId}`);
  }

  // ── Items ───────────────────────────────────────────────────────────────────

  /**
   * Fetches the full list of items. Used by the polling loop for periodic
   * reconciliation. Encapsulated here so it can later be swapped for a
   * delta-sync endpoint without touching components.
   */
  getItems(householdId: number, listId: number): Observable<ShoppingListItem[]> {
    return this.http.get<ShoppingListItem[]>(`${environment.apiUrl}/shopping-lists/${listId}/items`);
  }

  /**
   * Creates an item from raw user input. The server parses the input,
   * matches it against known products and returns the fully-populated
   * ShoppingListItem. The caller MUST supply a client-generated UUID so
   * retries are idempotent (the server deduplicates by `client_id`).
   */
  addItem(householdId: number, listId: number, body: AddItemRequest): Observable<ShoppingListItem> {
    return this.http.post<ShoppingListItem>(`${environment.apiUrl}/shopping-lists/${listId}/items`, body);
  }

  updateItem(householdId: number, listId: number, itemId: number, body: UpdateShoppingListItemRequest): Observable<ShoppingListItem> {
    return this.http.patch<ShoppingListItem>(`${environment.apiUrl}/shopping-list-items/${itemId}`, body);
  }

  /**
   * Reports whether a product is already a pending item on the household's
   * active shopping list. Used to show an "already on your list" hint.
   */
  getActiveListProduct(householdId: number, productId: number): Observable<ProductListPresence> {
    return this.http.get<ProductListPresence>(
      `${environment.apiUrl}/households/${householdId}/shopping-lists/active/by-product/${productId}`,
    );
  }


  // ── Trips ───────────────────────────────────────────────────────────────────
  getTrips(householdId: number, listId: number): Observable<ShoppingTrip[]> {
    return this.http.get<ShoppingTrip[]>(`${environment.apiUrl}/shopping-lists/${listId}/trips`);
  }

  getTrip(tripId: number): Observable<ShoppingTrip> {
    return this.http.get<ShoppingTrip>(`${environment.apiUrl}/shopping-trips/${tripId}`);
  }

  /**
   * Flattened, de-duplicated items bought across the most recent `limit`
   * completed trips. Powers quick-add autosuggestions.
   */
  getRecentTripItems(householdId: number, limit = 5): Observable<RecentTripItem[]> {
    const params = new HttpParams().set('limit', limit.toString());
    return this.http.get<RecentTripItem[]>(
      `${environment.apiUrl}/households/${householdId}/shopping-trips/recent/items`,
      { params },
    );
  }

  /**
   * Purchase suggestions aggregated across completed trips, ranked by how often
   * each item was bought. Includes free-text items (no linked product). Items
   * already pending on the active list are excluded server-side. Powers the
   * "Frequently Bought" suggestion block.
   */
  getFrequentlyBoughtItems(householdId: number, limit = 20): Observable<FrequentlyBoughtItem[]> {
    const params = new HttpParams().set('limit', limit.toString());
    return this.http.get<FrequentlyBoughtItem[]>(
      `${environment.apiUrl}/households/${householdId}/shopping-trips/frequent/items`,
      { params },
    );
  }

  // Trips are now created implicitly by the backend whenever an item flips
  // pending → done while no trip is active. There is no client-side
  // `startTrip` call any more.

  completeTrip(householdId: number, listId: number, tripId: number): Observable<ShoppingTrip> {
    return this.http.post<ShoppingTrip>(`${environment.apiUrl}/shopping-trips/${tripId}/complete`, {});
  }
}
